<script lang="ts" setup>
import type { Search } from '@/models/Search';
import type User from '@/models/User';

const { t } = useI18n();

const isAuthenticated = useAuthState();

const props = withDefaults(
  defineProps<{
    user?: null | User;
    search?: null | Search;
    showHousingType?: boolean;
    showLocation?: boolean;
    showValues?: null | boolean;
    showRentMax?: boolean;
    showSizeMin?: boolean;
    showRoomsMin?: boolean;
    showHousingProperties?: boolean;
    currency?: string;
  }>(),
  {
    user: null,
    search: null,
    showHousingType: true,
    showLocation: true,
    showValues: null,
    showRentMax: true,
    showSizeMin: true,
    showRoomsMin: true,
    showHousingProperties: false,
    currency: '',
  }
);

const housingType = props.search?.housingType || { id: 1, display: t(`filter.housing_type.items.1.name`) };
const city = props.search?.locations?.[0]?.cities?.[0]?.name || '';
const rentMax = isAuthenticated.value ? props.search?.rentMax : 0 || 123;
</script>

<template>
  <div class="tw-housing-card--search-info">
    <strong v-if="props.user" class="user">{{ $t(`housing_card.user_searches`, [props.user.displayName]) }}</strong>

    <template v-if="props.search">
      <span v-if="props.showHousingType" class="housing-type">{{ $t(`filter.housing_type.items.${housingType.id}.name`) }}</span>

      <span v-if="props.showLocation" class="location">{{ $t('housing_card.in_city', [city]) }}</span>

      <span v-if="props.showValues === true || (props.showValues === null && (props.showRentMax || props.showSizeMin || props.showRoomsMin))" class="values">
        <span>
          {{ $t('filter.prefix.max') }}
          <span :class="['rent-max', { blur: !isAuthenticated }]">{{ props.search.rentMax ? rentMax || 0 : '&infin;' }}</span>
          {{ currency }}
        </span>

        <span v-if="props.showSizeMin" class="size-min">{{ $t('housing_card.size_min', [$n(props.search.sizeMin || 0, 'area')]) }}</span>

        <span v-if="props.showRoomsMin" class="rooms-min">{{ $t('housing_card.rooms_min', [$n(props.search.roomsMin || 1, 'rooms')]) }}</span>
      </span>
    </template>
    <span v-else class="no-search">{{ $t('housing_card.no_active_search') }}</span>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/tw-variables.scss';

.tw-housing-card--search-info {
  display: flex;
  flex-wrap: wrap;
  gap: $tw-component--gap;
  padding: 0.75em 1em;
}

.values {
  display: inline-flex;
  flex-wrap: wrap;
  $gap: $tw-component--gap;
  gap: $gap;

  *:not(:last-child)::after {
    display: inline-block;
    content: '|';
    margin-left: $gap; // <- same as `.values gap`
  }

  .rent-max,
  .size-min,
  .rooms-min {
    display: inline-block;
    white-space: nowrap;
  }

  .blur {
    filter: blur(5px) !important;
    user-select: none !important;
  }
}
</style>
